import { useCallback, useEffect, useState } from "react";
import { api } from "../../../api/client";
import { Button, Empty, Icon, Modal, Spinner, cx } from "../../../components/ui";
import { namaBulan, waktuWIB } from "../logika";

// Pengaturan term cabang (team leader, keputusan #3, #14–#16): periode tiap term, status
// kuncinya, ubah bulan awal (selama term belum berisi apa pun), dan buka kunci 3 hari. Tiap
// pembukaan & perubahan periode tercatat dan ditampilkan di kartunya. Aturannya di server.

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September",
  "Oktober", "November", "Desember"];
const pad2 = (n) => String(n).padStart(2, "0");

/** "2026-11" → "Nov 2026 – Jan 2027" (periode 3 bulan mulai bulan itu). */
export function periodeDari(mulai) {
  const [y, m] = mulai.split("-").map(Number);
  const akhir = new Date(y, m - 1 + 2, 1);
  const iso = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
  const a = `${namaBulan(mulai)} ${y}`;
  const b = `${namaBulan(iso(akhir))} ${akhir.getFullYear()}`;
  return `${a} – ${b}`;
}

const FASE = { "akan-datang": "Akan datang", berjalan: "Berjalan", selesai: "Selesai" };

function Lencana({ children, warna }) {
  return <span className={cx("rounded-full px-2 py-0.5 text-[11px] font-black", warna)}>{children}</span>;
}

function KartuTerm({ t, sekarang, sibuk, onUbahPeriode, onBuka }) {
  const [ubah, setUbah] = useState(false);
  const [tahun, setTahun] = useState(t.code.slice(0, 4));
  const [bulan, setBulan] = useState(t.code.slice(5, 7));
  const [galat, setGalat] = useState("");
  const mulai = `${tahun}-${bulan}`;
  const y = Number(t.code.slice(0, 4));

  const simpan = async () => {
    setGalat("");
    const pesan = await onUbahPeriode(t, mulai);
    if (pesan) setGalat(pesan);
    else setUbah(false);
  };

  return (
    <section aria-label={t.name} className="anim-rise rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-black text-slate-900 dark:text-white">{t.name}</h3>
        <Lencana warna={t.id === sekarang ? "bg-primary/10 text-primary dark:text-[#3987e5]" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}>
          {FASE[t.fase]}
        </Lencana>
        {t.dibukaSampai ? (
          <Lencana warna="bg-amber-500/10 text-amber-700 dark:text-amber-300">Dibuka sampai {waktuWIB(t.dibukaSampai)}</Lencana>
        ) : t.terkunci ? (
          <Lencana warna="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">Terkunci</Lencana>
        ) : (
          <Lencana warna="bg-green-500/10 text-green-700 dark:text-green-300">Terbuka</Lencana>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Bulan 1 {namaBulan(t.bulan[0])} · Bulan 2 {namaBulan(t.bulan[1])} · Bulan 3 {namaBulan(t.bulan[2])}
        {" · "}
        {t.isi.nilai || t.isi.komentar || t.isi.terbit
          ? ["sudah berisi nilai", t.isi.terbit ? `${t.isi.terbit} raport terbit` : ""].filter(Boolean).join(" · ")
          : "belum berisi"}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        {t.bisaUbahPeriode ? (
          !ubah && (
            <Button variant="secondary" onClick={() => setUbah(true)} disabled={sibuk}>
              <Icon name="edit_calendar" />
              Ubah bulan awal
            </Button>
          )
        ) : (
          <p className="text-xs text-slate-500">Bulan awalnya tak bisa diubah lagi karena term ini sudah berisi.</p>
        )}
        {t.terkunci && (
          <Button variant="secondary" onClick={() => onBuka(t)} disabled={sibuk}>
            <Icon name="lock_open" />
            {t.dibukaSampai ? "Perpanjang 3 hari" : "Buka kunci 3 hari"}
          </Button>
        )}
      </div>

      {ubah && (
        <div className="mt-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs font-bold text-slate-500">
              Bulan awal
              <select
                value={bulan}
                onChange={(e) => setBulan(e.target.value)}
                className="mt-1 block rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              >
                {BULAN.map((b, i) => <option key={b} value={pad2(i + 1)}>{b}</option>)}
              </select>
            </label>
            <label className="text-xs font-bold text-slate-500">
              Tahun
              <select
                value={tahun}
                onChange={(e) => setTahun(e.target.value)}
                className="mt-1 block rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              >
                {[y - 1, y, y + 1].map((v) => <option key={v} value={String(v)}>{v}</option>)}
              </select>
            </label>
            <p className="pb-1.5 text-sm text-slate-600 dark:text-slate-300">→ <b>{periodeDari(mulai)}</b></p>
          </div>
          {galat && <p role="alert" className="mt-2 text-sm font-bold text-red-600">{galat}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={simpan} disabled={sibuk || mulai === t.code}>Simpan periode</Button>
            <Button variant="secondary" onClick={() => { setUbah(false); setGalat(""); }} disabled={sibuk}>Batal</Button>
          </div>
        </div>
      )}

      {t.catatan.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-500 dark:border-slate-800">
          {t.catatan.map((c, i) => (
            <li key={i}>
              {c.aksi === "buka" ? "Dibuka 3 hari" : `Periode diubah: ${c.detail}`} — {c.oleh}, {waktuWIB(c.pada)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function PengaturanTerm({ setToast, onUbah }) {
  const [data, setData] = useState(null);
  const [galat, setGalat] = useState("");
  const [tanyaBuka, setTanyaBuka] = useState(null);
  const [sibuk, setSibuk] = useState(false);

  const muat = useCallback(() => api.getRaportTermTL()
    .then((r) => { setData(r); setGalat(""); })
    .catch((err) => setGalat(err.message || "Daftar term gagal dimuat.")), []);
  useEffect(() => { muat(); }, [muat]);

  // Mengembalikan pesan galat (ditampilkan di kartunya) atau "" bila berhasil.
  const ubahPeriode = async (t, mulai) => {
    setSibuk(true);
    try {
      const r = await api.ubahPeriodeTerm(t.id, mulai);
      if (setToast) setToast({ type: "success", message: r.message || "Periode term diubah." });
      await muat();
      if (onUbah) onUbah();
      return "";
    } catch (err) {
      return err.message || "Periode term gagal diubah.";
    } finally {
      setSibuk(false);
    }
  };

  const bukaKunci = async () => {
    setSibuk(true);
    try {
      const r = await api.bukaKunciTerm(tanyaBuka.id);
      if (setToast) setToast({ type: "success", message: r.message || "Term dibuka 3 hari." });
      setTanyaBuka(null);
      await muat();
      if (onUbah) onUbah();
    } catch (err) {
      if (setToast) setToast({ type: "error", message: err.message || "Term gagal dibuka." });
    } finally {
      setSibuk(false);
    }
  };

  if (galat) return <Empty label={galat} />;
  if (!data) return <Spinner label="Memuat term…" />;

  return (
    <div className="space-y-4">
      <p className="anim-rise text-sm text-slate-500">
        Term terkunci otomatis sesudah bulan terakhirnya lewat. Buka kunci bila trainer perlu memperbaiki atau menerbitkan
        raport term itu: terbuka 3 hari, lalu terkunci lagi dengan sendirinya.
      </p>
      {[...data.term].reverse().map((t) => (
        <KartuTerm key={t.id} t={t} sekarang={data.sekarang} sibuk={sibuk} onUbahPeriode={ubahPeriode} onBuka={setTanyaBuka} />
      ))}

      <Modal title="Buka kunci term?" open={!!tanyaBuka} onClose={() => { if (!sibuk) setTanyaBuka(null); }}>
        {tanyaBuka && (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <b>{tanyaBuka.name}</b> akan terbuka selama 3 hari: trainer bisa mengubah isian dan menerbitkan ulang raport
              term ini. Sesudah itu terkunci lagi dengan sendirinya. Pembukaan ini tercatat atas namamu.
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={() => setTanyaBuka(null)} disabled={sibuk}>Batal</Button>
              <Button onClick={bukaKunci} disabled={sibuk}>
                <Icon name="lock_open" />
                {sibuk ? "Membuka…" : "Ya, buka 3 hari"}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
